import { memo } from "react"

import { CellProps, useFocusManagement } from "components/TreeState"
import { ChangedPlanRecord, SpotsPlan } from "../types"
import { checkBuyingStatus } from "utils/type-guards"
import { queryCache, useQuery } from "react-query"

import { Bulky } from "./Bulky"
import { CellError } from "./CellError"
import { DAYS_OF_WEEK } from "routes/Base/Admin/MarketVendors/Details/RateCard/RateCardDetails/BaseRate/utils"
import { MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import { MultiSelect } from "routes/Base/Admin/components/MultiSelect"
import { Paginated } from "api/types"
import { Tooltip } from "@material-ui/core"
import cloneDeep from "lodash/fp/cloneDeep"
import { css } from "@emotion/css"
import { fetchMediaConfig } from "api/media-types"
import { get } from "lodash"
import { set } from "lodash/fp"
import { useAuthenticated } from "providers/A12n"
import { useAutoCompute } from "../components/AutoCompute/useAutoCompute"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useToggle } from "hooks/useToggle"
import { useUndoStack } from "../useUndoStack"
import { utcToZonedTime } from "date-fns-tz"
import { useAutocomputeValidation } from "../useAutocomputeValidation"

export const DaysOfWeekCell = memo(function _DaysOfWeekCell(props: CellProps) {
  const { id: planId } = useParams<{ id: string }>()
  const { meta } = useAuthenticated()
  const { timeZone = "Etc/UTC" } = meta ?? {}
  const { rowId: id, dataKey, columnId, disabled } = props
  const { open, onOpen, onClose } = useToggle()
  const { t } = useI18n()
  const autoCompute = useAutoCompute()
  const tableSource = useTableSource()
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const writeTo = tableSource.state

  const action = readFrom[id]?.action
  const updatable = !action || action === "UPDATE_ORDER"
  const error = get(readError, `${id}.${columnId}`)
  const row = get(readFrom, `${id}`)
  const mediaId = readFrom[id]?.ancestorMediaId
  const value = (get(row, dataKey) || []) as Array<string>
  const readonly =
    readFrom[id]?.type === "media_subtype_plan" ||
    readFrom[id]?.type === "media_type_plan" ||
    readFrom[id]?.type === "package"

  const ps = new URLSearchParams([
    ["configScope", "MEDIA_PLAN"],
    ["configOwnerId", planId],
  ])

  const cached = queryCache.getQueryData<Paginated<MediaType>>([
    "media-types",
    ps.toString(),
  ])
  const { data: mediaTypes } = useQuery(
    ["media-types", ps.toString()],
    fetchMediaConfig,
    {
      enabled: !cached,
      initialData: cached,
      refetchOnMount: !cached,
    },
  )

  const mediaType = mediaTypes?.data?.find((mt) => mt.id === mediaId)
  const spotsPlanId = mediaType?.mediaFields?.find(
    (f) => f?.type === "SpotsPlan",
  )?.id
  const { spots = [], maxNumberOfSpots = 0 } = (get(
    readFrom,
    `${id}.planningMediaFields.${spotsPlanId}`,
  ) || {}) as SpotsPlan

  const buyingStatusVal = String(get(readFrom, `${id}.buyingStatus`))
  const buyingStatus = checkBuyingStatus(buyingStatusVal) ? buyingStatusVal : ""

  const handleChange = (v: Array<string>) => {
    const newv = v.length ? v : null
    globalUndoState.push([writeTo[id] as ChangedPlanRecord])
    writeTo[id] = set(`${dataKey}`, newv, writeTo[id])
    tableSource.clearError(id)
    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
  }

  const handleBlur = () => {
    if (spotsPlanId) {
      const spotsPlan: SpotsPlan = {
        maxNumberOfSpots: maxNumberOfSpots ?? 0,
        spots: [],
      }
      const updatedSpots = spots.filter((spot) => {
        const spotDate = utcToZonedTime(spot?.date, timeZone).getDay()
        const spotDay = DAYS_OF_WEEK[spotDate]
        return !!value?.length ? cloneDeep(value)?.includes(spotDay) : true
      })

      writeTo[id] = set(
        `planningMediaFields.${spotsPlanId}`,
        { ...spotsPlan, spots: updatedSpots },
        writeTo[id],
      )
    }
    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
    hasChanged(selected) &&
      autoCompute?.compute(writeTo[id] as ChangedPlanRecord)
  }

  const onToggleDay = (days: Array<Record<string, string>>) => {
    const v = days.map((d) => d.value)
    handleChange(v)
  }

  const onSelectAllDays = (doSelectAll: boolean) =>
    doSelectAll ? handleChange(DAYS_OF_WEEK) : handleChange([])

  const onClearDays = () => handleChange([])

  const { getInputProps } = useFocusManagement()
  const options = DAYS_OF_WEEK.map((d) => ({ name: t(`days.${d}`), value: d }))
  const sortedValue = DAYS_OF_WEEK.filter((d) => value.some((v) => v === d))
  const selected = sortedValue.map((d, idx) => ({
    index: idx,
    name: t(`days.${d}`),
    value: d,
  }))
  const inputValue = selected.map((s) => s.name.substring(0, 2)).join(" - ")

  const { handleFocus, hasChanged } = useAutocomputeValidation(selected, true)

  const isDisabled = buyingStatus === "POSTED_FOR_BILLING"

  return (
    <Bulky {...props}>
      <div
        style={{
          width: "100%",
          height: "100%",
          justifyContent: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Tooltip title={inputValue || ""} placement="bottom-start">
          <div className={styles.wrapper}>
            <MultiSelect
              {...getInputProps()}
              disabled={disabled || readonly || isDisabled}
              checkValues={() => true}
              items={options}
              open={open}
              onOpen={
                !disabled && !readonly && !isDisabled ? onOpen : undefined
              }
              onClose={onClose}
              getOptionLabel={(option: Record<string, string>) => option.name}
              selectedValues={selected || []}
              renderOption={(option: Record<string, string>) => option?.name}
              onToggleOption={onToggleDay}
              onClearOptions={onClearDays}
              onSelectAll={onSelectAllDays}
              selectAllValue="select-all"
              selectAllOption={{ name: t("filters.all"), value: "select-all" }}
              inputClassName={styles.input}
              inputValue={inputValue}
              onBlur={handleBlur}
              onFocus={handleFocus}
            />
            {error ? <CellError error={error} /> : null}
          </div>
        </Tooltip>
      </div>
    </Bulky>
  )
})

const styles = {
  wrapper: css`
    width: 100%;
  `,
  input: css`
    border-width: 0;
    width: 100%;
    .MuiInputBase-root {
      font-size: 13px;
    }
    &:hover {
      .MuiOutlinedInput-notchedOutline {
        border-width: 0;
      }
      border-width: 0;
    }
    .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-width: 0;
    }
    .MuiOutlinedInput-input {
      padding: 0;
      text-overflow: ellipsis;
    }
    fieldset {
      border-width: 0;
    }
  `,
}
